import {
  CSSProperties,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { Button } from "reactstrap";
import "quill-mention/autoregister";

import BrieIconOutlinedSrc from "assets/img/icons/common/icon_Brie_outlined.svg";
import { BrieComposeDrawer } from "../Widgets/RichText/BrieComposeDrawer";
import QuickNoteModal from "../Sidebar/QuickNote/QuickNoteModal";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import useMentionsModule from "./MentionsModule";
import TenantAPI from "../../api/api/TenantAPI";

const ReactQuill = lazy(() => import("react-quill-new"));

interface RichTextProps {
  isEditing: boolean;
  value: string;
  onChange: (text: string) => void;
  loading?: boolean;
  toggleEdit?: () => void;
  placeholder?: string;
  css?: string;
  style?: CSSProperties;
  useMentions?: boolean;
  editDisabled?: boolean;
  useHeader?: boolean;
  useStrikethrough?: boolean;
  useColor?: boolean;
  useBlock?: boolean;
  presentAsDocument?: boolean;
  useBrie?: boolean;
  hide?: boolean;
  /** Function called when open the Brie Command component */
  onOpenBrieCompose?: () => void;
  /** Function called when close the Brie Command component */
  onCloseBrieCompose?: () => void;
  /** Function called when using the voice button */
  onVoiceHandler?: () => void;
}

function RichTextEditorV2(props: RichTextProps) {
  return (
    <>
      <Suspense
        fallback={
          <div
            className="skeleton"
            style={{ width: "100%", height: 325, ...(props?.style || {}) }}
          ></div>
        }
      >
        <RichTextEditorV2Component {...props} />
      </Suspense>
    </>
  );
}

function RichTextEditorV2Component({
  isEditing,
  value,
  onChange,
  loading,
  toggleEdit,
  presentAsDocument,
  placeholder,
  css = "",
  style,
  useStrikethrough = true,
  useMentions = false,
  editDisabled = false,
  useHeader = true,
  useColor = true,
  useBlock = true,
  useBrie = true,
  hide = false,
  onOpenBrieCompose,
  onCloseBrieCompose,
  onVoiceHandler,
}: RichTextProps) {
  const id = useId();
  const valRef = useRef(value);
  valRef.current = value ?? "";
  const [useVoice, setVoice] = useState(false);
  const [openBrie, setBrie] = useState(false);
  const { mentionsModule } = useMentionsModule(!useMentions);
  const { formID, tenantID }: any = useParams();

  loading = loading || (useMentions && !mentionsModule);

  async function uploadImage(file: File | Blob) {
    let url = "";

    try {
      if (formID) {
        const response = await TenantAPI.uploadFileInForm({
          formID,
          tenantID,
          file,
          sources: undefined,
        });

        const item = response.data.data;
        const path = item.paths[0];
        const getUrlResult = await TenantAPI.getPublicFormsFileURL({
          formID,
          tenantID,
          fileName: path,
          expirationTimeSeconds: 31536000,
        });
        url = getUrlResult.data.data.url;
        return url;
      }

      const uploadFileResult = await TenantAPI.uploadFile({
        file,
        sources: undefined,
      });

      const fileName = uploadFileResult.data.data.paths[0];

      const getUrlResult = await TenantAPI.getFileURL(fileName, 31536000);

      url = getUrlResult.data.data.url;
    } catch (err) {
      console.log(err);
    }

    return url;
  }

  const imageHandler = useCallback(async () => {
    const input = document.createElement("input");

    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        const url = await uploadImage(file);

        const imgHTML = `<img src="${url}" />`;

        onChange(valRef.current.concat(imgHTML));
      }
    };
  }, []);

  function handleTranscriptSuccess(transcript: string) {
    onChange(valRef.current.concat(transcript));
  }

  const attachmentHandler = () => {
    const input = document.createElement("input");

    input.setAttribute("type", "file");
    input.click();

    input.onchange = async () => {
      if (input?.files) {
        const file = input.files[0];
        const url = await uploadImage(file);

        const attachmentHTML = `<div class="rich-text-attachment"><a href="${url}" rel="noopener noreferrer" target="_blank">${file.name}</a></div>`;

        onChange(valRef.current.concat(attachmentHTML));
      }
    };
  };

  const voiceHandler = useCallback(async () => {
    const input = document.createElement("input");
    input.click();
    setVoice(true);
    callFunctionIfExists(onVoiceHandler);
  }, []);

  const brieHandler = useCallback(async () => {
    const input = document.createElement("input");
    input.click();
    callFunctionIfExists(onOpenBrieCompose);
    setBrie(true);
  }, []);

  // For more options -> https://quilljs.com/docs/modules/toolbar/
  const modules = useMemo(() => {
    if (loading) return null;

    const fontOptions = ["bold", "italic", "underline"];

    if (useStrikethrough) {
      fontOptions.push("strike");
    }

    const container = [
      ...(useHeader ? [[{ header: [1, 2, 3, false] }]] : []),
      fontOptions,
      ...(useColor ? [[{ color: [] }, { background: [] }]] : []),
      ...(useBlock ? [["blockquote", "code-block"]] : []),
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
    ];

    return {
      mention: mentionsModule,
      toolbar: {
        container: container,
        handlers: {
          image: imageHandler,
        },
      },
    };
  }, [imageHandler, loading]);

  useEffect(() => {
    if (!isEditing || loading) return;

    const instance = document.getElementById(id)!;
    const allQuillFormats = instance.getElementsByClassName("ql-formats");
    const lastGroup = allQuillFormats[allQuillFormats.length - 1];

    // Attachment button
    const attachmentButton = document.createElement("button");
    attachmentButton.className = "d-flex align-items-center";
    attachmentButton.innerHTML =
      "<i class='mdi mdi-attachment' style='font-size:20px; margin-left: 2px' id='quill-attachments-button'/>";
    attachmentButton.onclick = attachmentHandler;

    // Dictation button
    const dictationButton = document.createElement("span");
    dictationButton.className = "cursor-pointer text-primary ml-2";
    dictationButton.innerHTML =
      "<i class='mdi mdi-microphone' style='font-size:20px;'/>";
    dictationButton.onclick = voiceHandler;

    // Brie Compose button
    const brieButton = document.createElement("button");
    brieButton.className = "d-flex align-items-center";
    brieButton.innerHTML = `<img src=${BrieIconOutlinedSrc} style='padding-top:5px;'}/>
      `;
    brieButton.onclick = brieHandler;

    // Wrapper to add Brie and Dictation buttons in the same span
    const wrapper = document.createElement("span");
    wrapper.className = "ql-formats cursor-pointer text-primary";

    if (!window.location.pathname.startsWith("/forms")) {
      wrapper.appendChild(dictationButton);
    }

    if (useBrie && isEditing) {
      wrapper.append(brieButton);
    }

    const toolbarDiv = instance?.querySelector(".ql-toolbar");
    var firstGroup = toolbarDiv?.querySelector("span:first-child");

    // Insert the new span element before the second child span element
    toolbarDiv?.insertBefore(wrapper, firstGroup!?.nextElementSibling);

    lastGroup?.appendChild(attachmentButton);
  }, [isEditing, loading]);

  // This to hide the BrieCompose drawer when the component is hidden.
  useEffect(() => {
    if (hide && openBrie) {
      setBrie(true);
    }
  }, [hide]);

  if (loading || !modules) {
    return (
      <div
        className="skeleton"
        style={{ width: "100%", height: 325, ...(style || {}) }}
      ></div>
    );
  }

  return (
    <>
      <div
        className={`${presentAsDocument ? "richtext-document" : ""} ${css}`}
        style={{ display: hide ? "none" : "block" }}
      >
        {isEditing ? (
          <>
            <ReactQuill
              id={id}
              value={value}
              onChange={onChange}
              modules={modules}
              theme="snow"
              placeholder={placeholder ? placeholder : undefined}
              preserveWhitespace={true}
              style={style}
            />

            {useVoice && (
              // @ts-ignore
              <QuickNoteModal
                open={useVoice}
                setOpen={setVoice}
                header="Brie Dictation"
                useBrieDication={true}
                saveTranscription={handleTranscriptSuccess}
                onClose={onCloseBrieCompose}
              />
            )}
          </>
        ) : value ? (
          <div className="ql-snow">
            <div
              className="ql-editor document-inner rich-text-preview"
              dangerouslySetInnerHTML={{
                __html: value,
              }}
            />
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center document-inner">
            <div className="my-4">This document is empty.</div>
            {!editDisabled && (
              <Button color="primary" onClick={toggleEdit}>
                Edit Document
              </Button>
            )}
          </div>
        )}
      </div>
      {useBrie && !hide && (
        <BrieComposeDrawer
          open={isEditing && openBrie}
          setOpen={setBrie}
          onSave={onChange}
          providedText={value}
          onDrawerClose={() => {
            callFunctionIfExists(onCloseBrieCompose);
          }}
        />
      )}
    </>
  );
}

export default RichTextEditorV2;
